/**
 * Block templates for freeform sections. Each template provides HTML that
 * includes explicit data-pb-editable attributes so blocks participate in
 * the standard click → panel/editor flow without relying on CodeStitch
 * class-based selector injection.
 *
 * Categories mirror Pagy's block menu: Basic, Media, Layout, Embed.
 */

/** Short random ID for unique element scoping. */
function shortId() {
  return crypto.randomUUID().slice(0, 6);
}

// ── Basic blocks ────────────────────────────────────────────────────

function paragraph() {
  return `<p class="cs-text" data-pb-editable="text" style="margin:0 0 1rem;line-height:1.6">Type your text here...</p>`;
}

function heading(level = 2) {
  const tag = `h${Math.max(1, Math.min(6, level))}`;
  return `<${tag} class="cs-title" data-pb-editable="text" style="margin:0 0 1rem">${level === 1 ? 'Page Heading' : level === 2 ? 'Section Heading' : 'Sub Heading'}</${tag}>`;
}

function bulletList() {
  return `<ul class="cs-text" style="margin:0 0 1rem;padding-left:1.5rem;line-height:1.8">
  <li data-pb-editable="text">List item one</li>
  <li data-pb-editable="text">List item two</li>
  <li data-pb-editable="text">List item three</li>
</ul>`;
}

function numberedList() {
  return `<ol class="cs-text" style="margin:0 0 1rem;padding-left:1.5rem;line-height:1.8">
  <li data-pb-editable="text">First item</li>
  <li data-pb-editable="text">Second item</li>
  <li data-pb-editable="text">Third item</li>
</ol>`;
}

function button() {
  return `<a class="cs-button-solid" href="#" style="display:inline-block;margin:0 0 1rem"><span data-pb-editable="button">Click Me</span></a>`;
}

function form() {
  const id = `form-${shortId()}`;
  return `<form id="${id}" style="display:flex;flex-direction:column;gap:0.75rem;max-width:28rem;margin:0 0 1rem">
  <input class="cs-input" type="text" placeholder="Name" style="padding:0.5rem 0.75rem;border:1px solid color-mix(in srgb,var(--bodyTextColor) 25%,transparent);border-radius:0.25rem;color:inherit;background:transparent">
  <input class="cs-input" type="email" placeholder="Email" style="padding:0.5rem 0.75rem;border:1px solid color-mix(in srgb,var(--bodyTextColor) 25%,transparent);border-radius:0.25rem;color:inherit;background:transparent">
  <textarea class="cs-input" placeholder="Message" rows="4" style="padding:0.5rem 0.75rem;border:1px solid color-mix(in srgb,var(--bodyTextColor) 25%,transparent);border-radius:0.25rem;color:inherit;background:transparent"></textarea>
  <button type="submit" class="cs-button-solid" style="align-self:flex-start"><span data-pb-editable="button">Submit</span></button>
</form>`;
}

// ── Media blocks ────────────────────────────────────────────────────

function image() {
  return `<img data-pb-editable="image" src="https://placehold.co/800x450?text=Image" alt="Image description" style="max-width:100%;height:auto;margin:0 0 1rem;border-radius:0.25rem">`;
}

function video() {
  return `<div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:0 0 1rem;border-radius:0.25rem">
  <iframe src="about:blank" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" allowfullscreen title="Video embed"></iframe>
</div>`;
}

function icon() {
  return `<svg data-pb-editable="svg" xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="margin:0 0 1rem">
  <circle cx="12" cy="12" r="10"/>
  <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
  <line x1="9" y1="9" x2="9.01" y2="9"/>
  <line x1="15" y1="9" x2="15.01" y2="9"/>
</svg>`;
}

function map() {
  return `<div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:0 0 1rem;border-radius:0.25rem;background:color-mix(in srgb,var(--bodyTextColor) 8%,transparent)">
  <iframe src="about:blank" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0" loading="lazy" title="Map embed"></iframe>
</div>`;
}

// ── Layout blocks ───────────────────────────────────────────────────

function row() {
  return `<div style="display:flex;gap:1rem;flex-wrap:wrap;margin:0 0 1rem">
  <div style="flex:1;min-width:200px;padding:1rem;background:color-mix(in srgb,var(--bodyTextColor) 6%,transparent);border-radius:0.25rem">
    <p class="cs-text" data-pb-editable="text">Column 1</p>
  </div>
  <div style="flex:1;min-width:200px;padding:1rem;background:color-mix(in srgb,var(--bodyTextColor) 6%,transparent);border-radius:0.25rem">
    <p class="cs-text" data-pb-editable="text">Column 2</p>
  </div>
</div>`;
}

function container() {
  return `<div style="max-width:48rem;margin:0 auto 1rem;padding:2rem;background:color-mix(in srgb,var(--bodyTextColor) 6%,transparent);border-radius:0.5rem">
  <p class="cs-text" data-pb-editable="text">Container content goes here...</p>
</div>`;
}

function card() {
  return `<div style="max-width:24rem;margin:0 0 1rem;border:1px solid color-mix(in srgb,var(--bodyTextColor) 15%,transparent);border-radius:0.5rem;overflow:hidden;box-shadow:0 1px 3px color-mix(in srgb,var(--bodyTextColor) 12%,transparent)">
  <img data-pb-editable="image" src="https://placehold.co/400x240?text=Card+Image" alt="Card image" style="width:100%;height:auto;display:block">
  <div style="padding:1.25rem">
    <h3 class="cs-h3" data-pb-editable="text" style="margin:0 0 0.5rem">Card Title</h3>
    <p class="cs-text" data-pb-editable="text" style="margin:0;color:var(--bodyTextColor)">Card description goes here with a brief summary.</p>
  </div>
</div>`;
}

function stack() {
  return `<div style="display:flex;flex-direction:column;gap:0.75rem;margin:0 0 1rem">
  <div style="padding:1rem;background:color-mix(in srgb,var(--bodyTextColor) 6%,transparent);border-radius:0.25rem">
    <p class="cs-text" data-pb-editable="text">Stack item 1</p>
  </div>
  <div style="padding:1rem;background:color-mix(in srgb,var(--bodyTextColor) 6%,transparent);border-radius:0.25rem">
    <p class="cs-text" data-pb-editable="text">Stack item 2</p>
  </div>
  <div style="padding:1rem;background:color-mix(in srgb,var(--bodyTextColor) 6%,transparent);border-radius:0.25rem">
    <p class="cs-text" data-pb-editable="text">Stack item 3</p>
  </div>
</div>`;
}

function divider() {
  return `<hr style="margin:1.5rem 0;border:none;border-top:1px solid color-mix(in srgb,var(--bodyTextColor) 20%,transparent)">`;
}

function accordion() {
  const id = `acc-${shortId()}`;
  return `<details style="margin:0 0 0.5rem;border:1px solid color-mix(in srgb,var(--bodyTextColor) 20%,transparent);border-radius:0.25rem;overflow:hidden">
  <summary class="cs-h3" data-pb-editable="text" style="padding:0.75rem 1rem;cursor:pointer;font-weight:600;background:color-mix(in srgb,var(--bodyTextColor) 6%,transparent)">Accordion heading</summary>
  <div style="padding:0.75rem 1rem">
    <p class="cs-text" data-pb-editable="text">Accordion content goes here...</p>
  </div>
</details>`;
}

// ── Embed blocks ────────────────────────────────────────────────────

function htmlEmbed() {
  return `<div style="margin:0 0 1rem;padding:1rem;border:2px dashed color-mix(in srgb,var(--bodyTextColor) 30%,transparent);border-radius:0.25rem;color:var(--bodyTextColor);font-family:monospace;font-size:0.875rem">
  &lt;!-- Paste your HTML embed code here --&gt;
</div>`;
}

// ── Block registry ──────────────────────────────────────────────────

/**
 * All available block types organized by category.
 * Each entry: { key, label, icon (emoji), create() → HTML string }
 */
export const BLOCK_CATEGORIES = [
  {
    name: 'Basic',
    blocks: [
      { key: 'paragraph', label: 'Paragraph', icon: '\u00B6', create: paragraph },
      { key: 'heading', label: 'Heading', icon: 'H', create: () => heading(2) },
      { key: 'bullet-list', label: 'Bulleted list', icon: '\u2022', create: bulletList },
      { key: 'numbered-list', label: 'Numbered list', icon: '#', create: numberedList },
      { key: 'button', label: 'Button', icon: '\u25A3', create: button },
      { key: 'form', label: 'Form', icon: '\u2610', create: form }
    ]
  },
  {
    name: 'Media',
    blocks: [
      { key: 'image', label: 'Image', icon: '\uD83D\uDDBC', create: image },
      { key: 'video', label: 'Video', icon: '\u25B6', create: video },
      { key: 'icon', label: 'Icon', icon: '\u2605', create: icon },
      { key: 'map', label: 'Map', icon: '\uD83D\uDDFA', create: map }
    ]
  },
  {
    name: 'Layout',
    blocks: [
      { key: 'row', label: 'Row', icon: '\u2503', create: row },
      { key: 'container', label: 'Container', icon: '\u25A1', create: container },
      { key: 'card', label: 'Card', icon: '\uD83C\uDFB4', create: card },
      { key: 'stack', label: 'Stack', icon: '\u2261', create: stack },
      { key: 'divider', label: 'Divider', icon: '\u2014', create: divider },
      { key: 'accordion', label: 'Accordion', icon: '\u25BC', create: accordion }
    ]
  },
  {
    name: 'Embed',
    blocks: [{ key: 'html', label: 'HTML', icon: '<>', create: htmlEmbed }]
  }
];

/**
 * Look up a block definition by key.
 * @param {string} key - Block type key (e.g., 'paragraph', 'image')
 * @returns {{ key: string, label: string, icon: string, create: () => string } | null}
 */
export function getBlockTemplate(key) {
  for (const cat of BLOCK_CATEGORIES) {
    const block = cat.blocks.find((b) => b.key === key);
    if (block) return block;
  }
  return null;
}
